import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import {
  getPanchayatDataProvider,
  type PanchayatBlockRecord,
  type PanchayatDistrictRecord,
  type PanchayatGramPanchayatRecord,
} from '@/lib/panchayat-data'

const INSTITUTION_SESSION_KEY = "institution_selection_v1"
const PERF_DEBUG_SESSION_ID = "slow-page-load"

function reportPerfDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PERF_DEBUG__ !== true) return
  if (typeof fetch !== "function") return
  const perfDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!perfDebugUrl) return
  fetch(perfDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PERF_DEBUG_SESSION_ID,
      runId: "pre-fix",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

function readInstitutionFromSession(): { district: string; block: string; village: string } {
  try {
    if (typeof window === "undefined") return { district: "", block: "", village: "" }
    const raw = sessionStorage.getItem(INSTITUTION_SESSION_KEY)
    if (!raw) return { district: "", block: "", village: "" }
    const parsed = JSON.parse(raw) as { district?: unknown; block?: unknown; village?: unknown }
    return {
      district: typeof parsed.district === "string" ? parsed.district : "",
      block: typeof parsed.block === "string" ? parsed.block : "",
      village: typeof parsed.village === "string" ? parsed.village : "",
    }
  } catch {
    return { district: "", block: "", village: "" }
  }
}

function writeInstitutionToSession(next: { district: string; block: string; village: string }) {
  try {
    if (typeof window === "undefined") return
    const normalized = {
      district: String(next.district || ""),
      block: String(next.block || ""),
      village: String(next.village || ""),
    }
    sessionStorage.setItem(INSTITUTION_SESSION_KEY, JSON.stringify(normalized))
  } catch {
    void 0
  }
}

interface LocationContextType {
  // Selection State
  selectedDistrict: string
  selectedBlock: string
  selectedVillage: string

  // Setters
  setSelectedDistrict: (district: string) => void
  setSelectedBlock: (block: string) => void
  setSelectedVillage: (village: string) => void

  // Data
  loading: boolean
  error: Error | null
  retry: () => void

  // Derived Lists
  districts: { id: string; names: { en: string; hi: string; pa: string } }[]
  blocks: { id: string; names: { en: string; hi: string; pa: string } }[]
  villages: { id: string; names: { en: string; hi: string; pa: string } }[]
}

const LocationContext = createContext<LocationContextType | undefined>(undefined)

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const initialInstitution = useMemo(() => readInstitutionFromSession(), [])
  const [selectedDistrict, setSelectedDistrict] = useState(initialInstitution.district)
  const [selectedBlock, setSelectedBlock] = useState(initialInstitution.block)
  const [selectedVillage, setSelectedVillage] = useState(initialInstitution.village)
  const provider = useMemo(() => getPanchayatDataProvider(), [])
  const [districtRecords, setDistrictRecords] = useState<PanchayatDistrictRecord[]>([])
  const [blockRecords, setBlockRecords] = useState<PanchayatBlockRecord[]>([])
  const [villageRecords, setVillageRecords] = useState<PanchayatGramPanchayatRecord[]>([])
  const [loadingDistricts, setLoadingDistricts] = useState(false)
  const [loadingBlocks, setLoadingBlocks] = useState(false)
  const [loadingVillages, setLoadingVillages] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const loadDistricts = useCallback(async () => {
    setLoadingDistricts(true)
    setError(null)
    try {
      const hierarchyStartMs = typeof performance !== "undefined" ? performance.now() : Date.now()
      const next = await provider.getDistricts()
      setDistrictRecords(next)
      reportPerfDebug("C", "src/contexts/LocationContext.tsx", "[DEBUG] District list loaded", {
        districtCount: next.length,
        elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - hierarchyStartMs,
      })
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error(String(nextError)))
    } finally {
      setLoadingDistricts(false)
    }
  }, [provider])

  const loadBlocks = useCallback(async (districtId: string) => {
    const normalizedDistrictId = String(districtId || "").trim()
    if (!normalizedDistrictId) {
      setBlockRecords([])
      setVillageRecords([])
      return
    }
    setLoadingBlocks(true)
    setError(null)
    try {
      const next = await provider.getBlocks(normalizedDistrictId)
      setBlockRecords(next)
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error(String(nextError)))
    } finally {
      setLoadingBlocks(false)
    }
  }, [provider])

  const loadVillages = useCallback(async (blockId: string, districtId: string) => {
    const normalizedBlockId = String(blockId || "").trim()
    const normalizedDistrictId = String(districtId || "").trim()
    if (!normalizedBlockId || !normalizedDistrictId) {
      setVillageRecords([])
      return
    }
    setLoadingVillages(true)
    setError(null)
    try {
      const next = await provider.getGramPanchayats(normalizedBlockId, normalizedDistrictId)
      setVillageRecords(next)
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error(String(nextError)))
    } finally {
      setLoadingVillages(false)
    }
  }, [provider])

  useEffect(() => {
    void loadDistricts()
  }, [loadDistricts])

  useEffect(() => {
    if (!selectedDistrict) {
      setBlockRecords([])
      setVillageRecords([])
      return
    }
    void loadBlocks(selectedDistrict)
  }, [loadBlocks, selectedDistrict])

  useEffect(() => {
    if (!selectedDistrict || !selectedBlock) {
      setVillageRecords([])
      return
    }
    void loadVillages(selectedBlock, selectedDistrict)
  }, [loadVillages, selectedBlock, selectedDistrict])

  useEffect(() => {
    if (!selectedDistrict || !selectedBlock || !selectedVillage) return
    let cancelled = false
    void provider
      .getInstitution(selectedVillage, selectedDistrict, selectedBlock)
      .then(() => {
        if (cancelled) return
      })
      .catch(() => {
        if (cancelled) return
      })
    return () => {
      cancelled = true
    }
  }, [provider, selectedBlock, selectedDistrict, selectedVillage])

  const retry = useCallback(() => {
    if (!selectedDistrict) {
      void loadDistricts()
      return
    }
    if (!selectedBlock) {
      void loadBlocks(selectedDistrict)
      return
    }
    void loadVillages(selectedBlock, selectedDistrict)
  }, [loadBlocks, loadDistricts, loadVillages, selectedBlock, selectedDistrict])

  const loading = loadingDistricts || loadingBlocks || loadingVillages

  const districts = useMemo(() => {
    return districtRecords.map((district) => ({
      id: district.id,
      names: district.names,
    }))
  }, [districtRecords])

  const blocks = useMemo(() => {
    return blockRecords.map((block) => ({
      id: block.id,
      names: block.names,
    }))
  }, [blockRecords])

  const villages = useMemo(() => {
    return villageRecords.map((village) => ({
      id: village.id,
      names: village.names,
    }))
  }, [villageRecords])

  // Reset dependent selections
  const handleSetDistrict = useCallback((district: string) => {
    setSelectedDistrict(district)
    setSelectedBlock('')
    setSelectedVillage('')
  }, [])

  const handleSetBlock = useCallback((block: string) => {
    setSelectedBlock(block)
    setSelectedVillage('')
  }, [])

  useEffect(() => {
    writeInstitutionToSession({ district: selectedDistrict, block: selectedBlock, village: selectedVillage })
  }, [selectedBlock, selectedDistrict, selectedVillage])

  const value = useMemo(() => ({
    selectedDistrict,
    selectedBlock,
    selectedVillage,
    setSelectedDistrict: handleSetDistrict,
    setSelectedBlock: handleSetBlock,
    setSelectedVillage,
    loading,
    error,
    retry,
    districts,
    blocks,
    villages
  }), [
    selectedDistrict, selectedBlock, selectedVillage,
    handleSetDistrict, handleSetBlock, setSelectedVillage,
    loading, error, retry,
    districts, blocks, villages
  ])

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  )
}

export function useLocationState() {
  const context = useContext(LocationContext)
  if (context === undefined) {
    throw new Error('useLocationState must be used within a LocationProvider')
  }
  return context
}
